import { PaymentIntent, SetupIntent, StripeError } from '@stripe/stripe-js';
import { isNil, reject } from 'ramda';

import assertEventType from 'common/bento/lib/assertEventType';
import { MachineServices } from 'common/bento/types/MachineConfig';
import { apolloClient } from 'common/graphql/clients';
import { logEvent } from 'features/Analytics/analytics';
import { OBError } from 'features/Bento/libs/errors/OBError';
import { AccountStatusDocument } from 'features/Onboarding/common/useAccountStatus/graphql/queries/AccountStatus.gql';
import { AccountStatusPrimaryApplicantDocument } from 'features/Onboarding/common/useAccountStatus/graphql/queries/AccountStatusPrimaryApplicant.gql';
import { getBillingDetails } from 'features/Stripe';
import logger from 'helpers/logger';

import { CreateSasuCreationPaymentIntentDocument } from '../graphql/mutations/createSasuCreationPaymentIntent.gql';
import { PaySasuCreationDocument } from '../graphql/mutations/paySasuCreation.gql';
import { SavePricingPlanSelectionDocument } from '../graphql/mutations/savePricingPlanSelection.gql';
import { Model } from './model';

export enum Service {
  PayPackage = 'payPackage',
  RegisterPricingPlanSelection = 'registerPricingPlanSelection',
}

const services: MachineServices<Model, Service> = {
  payPackage: async ({ getModuleData, selectedPackage }, event) => {
    assertEventType(event, 'PAY');

    if (!selectedPackage) {
      throw new Error('selectedPackage is required');
    }

    const {
      stripe,
      viewer: { company, profile },
    } = getModuleData();

    const { paymentMethod } = await stripe.createPaymentMethod({
      billing_details: getBillingDetails(reject(isNil, profile)),
      card: event.data.cardElement,
      type: 'card',
    });
    if (!paymentMethod) {
      throw new Error('INVALID_FORM');
    }

    const { data } = await apolloClient.mutate({
      mutation: CreateSasuCreationPaymentIntentDocument,
      variables: {
        input: {
          companyProfileId: company.companyProfileId,
          packageId: selectedPackage.providerPackageId,
          paymentMethodId: paymentMethod.id,
        },
      },
    });

    const paymentIntent = data?.createSasuCreationPaymentIntent;

    if (!paymentIntent) {
      throw new Error('Unable to get payment intent from server');
    }

    if (!paymentIntent.readyForCapture) {
      let cardOperationResult: SetupIntent | PaymentIntent | undefined;
      let error: StripeError | undefined;

      if (paymentIntent.setupIntentId) {
        ({ error: error, setupIntent: cardOperationResult } =
          await stripe.confirmCardSetup(paymentIntent.clientSecret, {
            payment_method: paymentMethod.id,
          }));
      } else {
        ({ error: error, paymentIntent: cardOperationResult } =
          await stripe.confirmCardPayment(paymentIntent.clientSecret, {
            payment_method: paymentMethod.id,
          }));
      }

      if (error) {
        if (error.code === 'payment_intent_authentication_failure') {
          logEvent({
            name: 'SASU_CREATION_Payment_3DS_Error',
          });
        } else if (!error.code || error.code === 'processing_error') {
          logEvent({
            name: 'SASU_CREATION_Payment_Other_Error',
          });
          logger.error(error);
        }

        /**
         * The localizedMessage will be picked up by `shared.actions.error()`
         */
        throw new OBError(error.code ?? 'Unknown Stripe error', {
          localizedMessage: error.message,
        });
      }

      if (!cardOperationResult) {
        throw new Error('Unknown payment error (empty cardOperationResult)');
      }

      if (
        cardOperationResult.status === 'requires_payment_method' ||
        cardOperationResult.status === 'requires_action'
      ) {
        logger.error(
          `3DS failure: ${cardOperationResult.status}, ${cardOperationResult.cancellation_reason}`,
        );
        throw new Error(`3DS failure`);
      }

      if (
        cardOperationResult.status !== 'requires_capture' &&
        cardOperationResult.status !== 'succeeded'
      ) {
        logger.error(
          `Unexpected status for payment intent after 3DS flow: ${cardOperationResult.status}, ${cardOperationResult.cancellation_reason}`,
        );

        throw new Error(
          `Unexpected status for payment intent after 3DS flow ${cardOperationResult.status}`,
        );
      }
    }

    return apolloClient.mutate({
      mutation: PaySasuCreationDocument,
      variables: {
        input: {
          companyProfileId: company.companyProfileId,
          packageId: selectedPackage.providerPackageId,
          paymentIntentId: paymentIntent.paymentIntentId,
        },
      },
    });
  },

  registerPricingPlanSelection: (context) => {
    const { companyProfileId } = context.getModuleData().viewer.company;
    const { selectedPlan } = context;

    if (selectedPlan === null) {
      throw new Error('selectedPlanId cannot be null');
    }

    return apolloClient.mutate({
      awaitRefetchQueries: true,
      mutation: SavePricingPlanSelectionDocument,
      refetchQueries: [
        {
          query: AccountStatusDocument,
          variables: {
            companyProfileId,
          },
        },
        {
          query: AccountStatusPrimaryApplicantDocument,
          variables: {
            companyProfileId,
          },
        },
      ],
      variables: {
        company: {
          companyProfileId,
          selectedPlanId: selectedPlan.providerPlanId,
        },
      },
    });
  },
};

export default services;
